// Recurring bills: schedules with template lines. The generate run drafts
// one standard bill per due occurrence, receives it, and posts the standard
// bill journals — no bespoke posting.
import { formatMoney } from "@sylviq/ui-core";
import { Button } from "@sylviq/ui-react-web/components/ui/button";
import { Dialog } from "@sylviq/ui-react-web/components/ui/dialog";
import { Drawer } from "@sylviq/ui-react-web/components/ui/drawer";
import { Input } from "@sylviq/ui-react-web/components/ui/input";
import { PageHeader } from "@sylviq/ui-react-web/components/ui/page-header";
import {
  StatusDot,
  type StatusTone,
} from "@sylviq/ui-react-web/components/ui/status-dot";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@sylviq/ui-react-web/components/ui/table";
import { Link } from "@tanstack/react-router";
import { type FormEvent, useEffect, useState } from "react";
import { AccountPicker } from "@/components/Pickers";
import {
  useAnyHeldPlanGenerate,
  useAnyPlanGeneratePending,
  usePlanGenerateScope,
} from "@/components/plan-generate-recovery";
import { CardField } from "@/components/RecordCard";
import {
  clearDocumentDraft,
  emptyDraftLine,
  loadDocumentDraft,
  newDocumentDraft,
  saveDocumentDraft,
  type DocumentDraft,
} from "@/features/document-lines/documentDraft";
import {
  type CreateRecurringBillLineInput,
  createRecurringBillSchedule,
  generateRecurringBills,
  listRecurringBillSchedules,
  RECURRING_FREQUENCIES,
  type RecurringBillGenerateResult,
  type RecurringBillSchedule,
} from "@/lib/api";
import { useOrganization } from "@/lib/organization";

const RECURRING_BILL_DRAFT_PREFIX = "niche-accounting.recurring-bill-draft.v1";

function statusTone(status: string): StatusTone {
  switch (status.toLowerCase()) {
    case "active":
      return "success";
    case "paused":
      return "warning";
    case "completed":
      return "idle";
    default:
      return "idle";
  }
}

function plainStatus(status: string): string {
  const lower = status.toLowerCase();
  return lower.charAt(0).toUpperCase() + lower.slice(1);
}

function plainFrequency(frequency: string): string {
  return frequency
    .split("_")
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(" ");
}

function toErrorMessage(cause: unknown): string {
  return cause instanceof Error ? cause.message : String(cause);
}

function scheduleCurrency(currency: string | undefined): string {
  return currency || "USD";
}

function toMinor(amount: string): number | null {
  const trimmed = amount.trim();
  if (!trimmed) return null;
  const value = Number(trimmed);
  if (!Number.isFinite(value) || value <= 0) return null;
  const minor = Math.round(value * 100);
  return Number.isSafeInteger(minor) && minor > 0 ? minor : null;
}

function todayInput(): string {
  return new Date().toISOString().slice(0, 10);
}

// Schedule-only fields live beside the document-draft mirror: frequency and
// window are not part of DocumentDraft, so they stay in page state.
interface ScheduleDraftFields {
  frequency: string;
  startDate: string;
  endDate: string;
  dueInDays: string;
  maxOccurrences: string;
}

function newScheduleDraftFields(): ScheduleDraftFields {
  return {
    frequency: "monthly",
    startDate: todayInput(),
    endDate: "",
    dueInDays: "14",
    maxOccurrences: "",
  };
}

function describeRunResult(result: RecurringBillGenerateResult): string {
  if (result.generated_count === 0) {
    return "Nothing was due — no bills generated.";
  }
  const noun = result.generated_count === 1 ? "bill" : "bills";
  const ids = result.generated.map((row) => row.bill_id).filter(Boolean);
  return `Generated ${result.generated_count} ${noun}.${
    ids.length > 0 ? ` Bill ids: ${ids.join(", ")}.` : ""
  }`;
}

export function RecurringBillsPage({
  embedded = false,
}: {
  embedded?: boolean;
}) {
  const { scope } = useOrganization();
  const organizationId = scope?.organization_id ?? "default";

  const [schedules, setSchedules] = useState<RecurringBillSchedule[] | null>(
    null,
  );
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [drawer, setDrawer] = useState(false);
  const [running, setRunning] = useState(false);
  const [confirmRun, setConfirmRun] = useState(false);
  const planScope = usePlanGenerateScope();
  const generateHeld = useAnyHeldPlanGenerate(planScope);
  const generatePending = useAnyPlanGeneratePending(planScope);

  // Client-side table controls over the fetched list (the list endpoint has
  // no search/status params — filtering happens here).
  const [statusTab, setStatusTab] = useState("");
  const [search, setSearch] = useState("");

  // Draft mirror: load from this browser session when the organization is known,
  // then save on every change. A failed storage read yields a fresh draft.
  const [draft, setDraft] = useState<DocumentDraft>(newDocumentDraft);
  const [loadedDraftKey, setLoadedDraftKey] = useState<string | null>(null);
  const [extra, setExtra] = useState<ScheduleDraftFields>(
    newScheduleDraftFields,
  );
  const [submitting, setSubmitting] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);

  useEffect(() => {
    setDraft(
      loadDocumentDraft(RECURRING_BILL_DRAFT_PREFIX, organizationId) ??
        newDocumentDraft(),
    );
    setLoadedDraftKey(organizationId);
  }, [organizationId]);
  useEffect(() => {
    if (loadedDraftKey !== organizationId) return;
    saveDocumentDraft(RECURRING_BILL_DRAFT_PREFIX, organizationId, draft);
  }, [draft, loadedDraftKey, organizationId]);

  const reload = () => {
    listRecurringBillSchedules()
      .then((rows) => {
        setSchedules(rows);
        setError(null);
      })
      .catch((cause: unknown) => setError(toErrorMessage(cause)));
  };

  useEffect(reload, []);

  const updateDraft = (patch: Partial<DocumentDraft>) =>
    setDraft((prev) => ({ ...prev, ...patch }));

  const updateLine = (
    index: number,
    patch: Partial<DocumentDraft["lines"][number]>,
  ) => {
    setDraft((prev) => ({
      ...prev,
      lines: prev.lines.map((line, i) =>
        i === index ? { ...line, ...patch } : line,
      ),
    }));
  };

  const removeLine = (index: number) => {
    setDraft((prev) => ({
      ...prev,
      lines:
        prev.lines.length <= 1
          ? prev.lines
          : prev.lines.filter((_, i) => i !== index),
    }));
  };

  const updateExtra = (patch: Partial<ScheduleDraftFields>) =>
    setExtra((prev) => ({ ...prev, ...patch }));

  // The supplier field doubles as the browser-local contact link: picking a
  // supplier already used by a plan keeps that plan's linked CRM contact,
  // and a typed name that matches no known plan stays unlinked.
  const updateSupplierName = (value: string) => {
    const key = value.trim().toLowerCase();
    const linked = key
      ? (schedules ?? []).find(
          (schedule) =>
            Boolean(schedule.crm_contact_id) &&
            schedule.supplier_name.trim().toLowerCase() === key,
        )
      : undefined;
    updateDraft({
      partyName: value,
      crmContactId: linked?.crm_contact_id ?? "",
    });
  };

  const knownSupplierNames = [
    ...new Set(
      (schedules ?? [])
        .map((schedule) => schedule.supplier_name.trim())
        .filter(Boolean),
    ),
  ];

  const clearDraft = () => {
    setDraft(newDocumentDraft());
    clearDocumentDraft(RECURRING_BILL_DRAFT_PREFIX, organizationId);
    setExtra(newScheduleDraftFields());
    setFormError(null);
  };

  const runNow = async () => {
    setRunning(true);
    setError(null);
    setNotice(null);
    try {
      const result = await generateRecurringBills();
      setNotice(describeRunResult(result));
      setConfirmRun(false);
      reload();
    } catch (cause) {
      setError(toErrorMessage(cause));
      setConfirmRun(false);
    } finally {
      setRunning(false);
    }
  };

  const submit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setSubmitting(true);
    setFormError(null);
    try {
      const name = draft.partyName.trim();
      if (!name) throw new Error("Supplier name is required.");
      if (!extra.startDate) throw new Error("Start date is required.");
      const due = Number(extra.dueInDays);
      if (!Number.isInteger(due) || due < 0 || due > 3650)
        throw new Error("Payment terms must be 0–3650 days after billing.");
      const max = extra.maxOccurrences.trim()
        ? Number(extra.maxOccurrences.trim())
        : null;
      if (max !== null && (!Number.isInteger(max) || max <= 0))
        throw new Error("Max bills must be a positive whole number.");
      if (draft.lines.length === 0) throw new Error("Add at least one line.");
      const payloadLines: CreateRecurringBillLineInput[] = draft.lines.map(
        (line, i) => {
          if (!line.account_id)
            throw new Error(`Line ${i + 1}: pick an expense account.`);
          if (!line.description.trim())
            throw new Error(`Line ${i + 1}: description is required.`);
          const amount_minor = toMinor(line.amount);
          if (amount_minor === null)
            throw new Error(
              `Line ${i + 1}: amount must be a positive number (e.g. 12.50).`,
            );
          return {
            account_id: line.account_id,
            description: line.description.trim(),
            amount_minor,
          };
        },
      );
      await createRecurringBillSchedule({
        supplier_name: name,
        ...(draft.crmContactId ? { crm_contact_id: draft.crmContactId } : {}),
        frequency: extra.frequency,
        start_date: extra.startDate,
        ...(extra.endDate ? { end_date: extra.endDate } : {}),
        ...(max !== null ? { max_occurrences: max } : {}),
        due_in_days: due,
        lines: payloadLines,
      });
      clearDraft();
      setDrawer(false);
      reload();
    } catch (cause) {
      setFormError(toErrorMessage(cause));
    } finally {
      setSubmitting(false);
    }
  };

  const templateTotal = (schedule: RecurringBillSchedule): number =>
    schedule.lines.reduce((sum, line) => sum + line.amount_minor, 0);

  const statusCounts = (schedules ?? []).reduce<Record<string, number>>(
    (counts, schedule) => {
      const key = schedule.status.toLowerCase();
      counts[key] = (counts[key] ?? 0) + 1;
      return counts;
    },
    {},
  );
  // Client-side filter over the fetched list.
  const visibleSchedules = (schedules ?? [])
    .filter(
      (schedule) => !statusTab || schedule.status.toLowerCase() === statusTab,
    )
    .filter((schedule) => {
      const query = search.trim().toLowerCase();
      if (!query) return true;
      return schedule.supplier_name.toLowerCase().includes(query);
    });

  const previewMinor = draft.lines.reduce(
    (sum, line) => sum + (toMinor(line.amount) ?? 0),
    0,
  );

  const actions = (
    <>
      <Button
        type="button"
        variant="secondary"
        onClick={() => setConfirmRun(true)}
        disabled={running || !!generateHeld || generatePending}
        title={
          generatePending
            ? "A plan generation is in progress — wait for it to finish before running due plans"
            : generateHeld
              ? `An earlier generation for ${generateHeld.partyName} was not confirmed — open that plan and check current status first`
              : "Generate bills for every plan due as of today across the whole organization"
        }
      >
        {running ? "Running…" : "Run due plans"}
      </Button>
      <Button type="button" onClick={() => setDrawer(true)}>
        New plan
      </Button>
    </>
  );

  return (
    <div className={embedded ? "grid gap-4" : "grid gap-6"}>
      {embedded ? (
        <div className="flex flex-wrap items-center justify-end gap-2">
          {actions}
        </div>
      ) : (
        <PageHeader
          title="Recurring bills"
          description="Repeat supplier costs on a schedule — each run creates a normal bill for every plan that is due."
          actions={actions}
        />
      )}

      {error ? (
        <div
          role="alert"
          className="surface rounded-xl border border-[var(--danger)] p-4 text-sm text-[var(--danger)]"
        >
          {error}
          <div className="mt-2">
            <Button
              type="button"
              variant="secondary"
              size="sm"
              onClick={reload}
            >
              Retry
            </Button>
          </div>
        </div>
      ) : null}

      {notice ? (
        <output className="surface block rounded-xl border border-[var(--success)] p-4 text-sm text-[var(--success)]">
          {notice}
        </output>
      ) : null}

      {generatePending ? (
        <p
          role="status"
          className="surface rounded-xl border border-[var(--border)] p-4 text-sm"
        >
          A plan generation is in progress. Run due plans is blocked until it
          finishes.
        </p>
      ) : generateHeld ? (
        <p
          role="status"
          className="surface rounded-xl border border-[var(--border)] p-4 text-sm"
        >
          A generation for {generateHeld.partyName} was not confirmed. Open
          that plan and check current status before running due plans.
        </p>
      ) : null}

      <div className="surface grid gap-3 rounded-2xl p-4">
        <div className="flex flex-wrap items-end gap-3">
          <label
            htmlFor="recurring-bills-search"
            className="grid min-w-40 flex-1 gap-1 text-xs font-medium"
          >
            Search
            <Input
              id="recurring-bills-search"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Supplier…"
            />
          </label>
          <label
            htmlFor="recurring-bills-status"
            className="grid min-w-40 gap-1 text-xs font-medium"
          >
            Status
            <select
              id="recurring-bills-status"
              className="select"
              value={statusTab}
              onChange={(e) => setStatusTab(e.target.value)}
            >
              <option value="">
                All statuses ({(schedules ?? []).length})
              </option>
              <option value="active">
                Active ({statusCounts.active ?? 0})
              </option>
              <option value="paused">
                Paused ({statusCounts.paused ?? 0})
              </option>
              <option value="completed">
                Completed ({statusCounts.completed ?? 0})
              </option>
            </select>
          </label>
          <Button type="button" variant="secondary" onClick={reload}>
            Refresh
          </Button>
        </div>
      </div>

        <div
          className="surface hidden overflow-hidden rounded-2xl min-[64rem]:block"
          data-testid="recurring-bills-table"
        >
          <div className="table-scroll">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Supplier</TableHead>
                  <TableHead>Every</TableHead>
                  <TableHead>Next run</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="text-right">Created so far</TableHead>
                  <TableHead className="text-right">Per bill</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {schedules === null ? (
                  <TableRow>
                    <TableCell colSpan={6}>Loading…</TableCell>
                  </TableRow>
                ) : schedules.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={6}>
                      No repeat-cost plans yet — create the first one above.
                    </TableCell>
                  </TableRow>
                ) : visibleSchedules.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={6}>
                      No plans match these filters.
                    </TableCell>
                  </TableRow>
                ) : (
                  visibleSchedules.map((schedule) => (
                    <TableRow key={schedule.id}>
                      <TableCell>
                        <Link
                          to="/recurring-bills/$recurringBillScheduleId"
                          params={{ recurringBillScheduleId: schedule.id }}
                          className="font-medium hover:underline"
                        >
                          {schedule.supplier_name}
                        </Link>
                      </TableCell>
                      <TableCell>{plainFrequency(schedule.frequency)}</TableCell>
                      <TableCell className="whitespace-nowrap">
                        {schedule.next_run_date
                          ? new Date(schedule.next_run_date).toLocaleDateString()
                          : "—"}
                      </TableCell>
                      <TableCell>
                        <StatusDot
                          tone={statusTone(schedule.status)}
                          label={plainStatus(schedule.status)}
                        />
                      </TableCell>
                      <TableCell className="text-right tabular-nums">
                        {schedule.occurrences_generated}
                        {schedule.max_occurrences
                          ? ` of ${schedule.max_occurrences}`
                          : ""}
                      </TableCell>
                      <TableCell className="text-right tabular-nums">
                        {formatMoney({
                          cents: templateTotal(schedule),
                          currency: scheduleCurrency(schedule.currency_code),
                        })}
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>
        </div>

        <div
          className="stack-list grid gap-3 min-[64rem]:hidden"
          data-testid="recurring-bills-stack"
        >
          {schedules === null ? (
            <p className="text-sm text-[var(--muted-foreground)]">Loading…</p>
          ) : visibleSchedules.length === 0 ? (
            <p className="text-sm text-[var(--muted-foreground)]">
              {schedules.length === 0
                ? "No repeat-cost plans yet — create the first one."
                : "No plans match these filters."}
            </p>
          ) : (
            visibleSchedules.map((schedule) => (
              <article
                key={schedule.id}
                className="stack-card rounded-xl border border-[var(--border)] bg-[var(--muted)] p-4"
              >
                <div className="stack-card-heading flex flex-wrap items-center justify-between gap-4 text-sm">
                  <Link
                    to="/recurring-bills/$recurringBillScheduleId"
                    params={{ recurringBillScheduleId: schedule.id }}
                    className="record-title underline-offset-4 hover:underline"
                  >
                    {schedule.supplier_name}
                  </Link>
                  <span className="tabular-nums">
                    {formatMoney({
                      cents: templateTotal(schedule),
                      currency: scheduleCurrency(schedule.currency_code),
                    })}
                  </span>
                </div>
                <CardField label="Every">
                  {plainFrequency(schedule.frequency)}
                </CardField>
                <CardField label="Next run">
                  {schedule.next_run_date
                    ? new Date(schedule.next_run_date).toLocaleDateString()
                    : "—"}
                </CardField>
                <CardField label="Status">
                  <StatusDot
                    tone={statusTone(schedule.status)}
                    label={plainStatus(schedule.status)}
                  />
                </CardField>
                <CardField label="Created so far">
                  {schedule.occurrences_generated}
                  {schedule.max_occurrences
                    ? ` of ${schedule.max_occurrences}`
                    : ""}
                </CardField>
                <div className="flex flex-wrap gap-2 pt-3">
                  <Button asChild variant="secondary" size="sm">
                    <Link
                      to="/recurring-bills/$recurringBillScheduleId"
                      params={{ recurringBillScheduleId: schedule.id }}
                    >
                      Open plan
                    </Link>
                  </Button>
                </div>
              </article>
            ))
          )}
        </div>

      <Drawer
        open={drawer}
        onOpenChange={setDrawer}
        title="New repeat-cost plan"
        description="Template lines carry plain amounts on expense accounts — tax is worked out when each bill is created. Closing this panel keeps your draft in this browser session."
        className="w-[min(46rem,100%)]"
        footer={
          <>
            <Button
              type="button"
              variant="secondary"
              onClick={() => setDrawer(false)}
            >
              Back to plans
            </Button>
            {draft.partyName ||
            draft.lines.some((line) => line.description || line.amount) ? (
              <Button type="button" variant="ghost" onClick={clearDraft}>
                Discard draft
              </Button>
            ) : null}
            <Button
              type="submit"
              form="recurring-bill-create-form"
              disabled={submitting}
              className="ms-auto"
            >
              {submitting ? "Creating…" : "Create plan"}
            </Button>
          </>
        }
      >
        <form
          id="recurring-bill-create-form"
          onSubmit={submit}
          className="grid gap-3"
          aria-label="Create repeat-cost plan"
        >
          {formError ? (
            <p role="alert" className="text-sm text-[var(--danger)]">
              {formError}
            </p>
          ) : null}
          <label
            htmlFor="rec-bill-supplier"
            className="grid gap-1 text-xs font-medium"
          >
            Supplier name
            <Input
              id="rec-bill-supplier"
              list="rec-bill-supplier-options"
              value={draft.partyName}
              onChange={(e) => updateSupplierName(e.target.value)}
              required
              placeholder="Acme Supplies"
              disabled={submitting}
            />
            <datalist id="rec-bill-supplier-options">
              {knownSupplierNames.map((name) => (
                <option key={name} value={name} />
              ))}
            </datalist>
          </label>
          <div className="grid gap-3 sm:grid-cols-2">
            <label
              htmlFor="rec-bill-freq"
              className="grid gap-1 text-xs font-medium"
            >
              How often
              <select
                id="rec-bill-freq"
                className="select"
                value={extra.frequency}
                onChange={(e) => updateExtra({ frequency: e.target.value })}
                disabled={submitting}
              >
                {RECURRING_FREQUENCIES.map((option) => (
                  <option key={option} value={option}>
                    {plainFrequency(option)}
                  </option>
                ))}
              </select>
            </label>
            <label
              htmlFor="rec-bill-due"
              className="grid gap-1 text-xs font-medium"
            >
              Due (days after bill)
              <Input
                id="rec-bill-due"
                type="number"
                value={extra.dueInDays}
                onChange={(e) => updateExtra({ dueInDays: e.target.value })}
                min="0"
                max="3650"
                step="1"
                required
                disabled={submitting}
              />
            </label>
            <label
              htmlFor="rec-bill-start"
              className="grid gap-1 text-xs font-medium"
            >
              Starts
              <Input
                id="rec-bill-start"
                type="date"
                value={extra.startDate}
                onChange={(e) => updateExtra({ startDate: e.target.value })}
                required
                disabled={submitting}
              />
            </label>
            <label
              htmlFor="rec-bill-end"
              className="grid gap-1 text-xs font-medium"
            >
              Ends (optional)
              <Input
                id="rec-bill-end"
                type="date"
                value={extra.endDate}
                onChange={(e) => updateExtra({ endDate: e.target.value })}
                disabled={submitting}
              />
            </label>
          </div>
          <label
            htmlFor="rec-bill-max"
            className="grid gap-1 text-xs font-medium"
          >
            Max bills (optional — the plan finishes after this many)
            <Input
              id="rec-bill-max"
              type="number"
              value={extra.maxOccurrences}
              onChange={(e) => updateExtra({ maxOccurrences: e.target.value })}
              min="1"
              step="1"
              placeholder="Unlimited"
              disabled={submitting}
            />
          </label>
          <fieldset className="grid gap-2">
            <legend className="text-xs font-medium">Billed each time</legend>
            {draft.lines.map((line, index) => (
              <div key={line.uid} className="grid gap-2">
                <label
                  htmlFor={`rec-bill-line-${index}-account`}
                  className="grid gap-1 text-xs font-medium"
                >
                  Expense account
                  <AccountPicker
                    id={`rec-bill-line-${index}-account`}
                    value={line.account_id}
                    onChange={(account_id) => updateLine(index, { account_id })}
                    disabled={submitting}
                    required
                  />
                </label>
                <div className="grid gap-2 sm:grid-cols-[2fr_1fr_auto] sm:items-end">
                  <label
                    htmlFor={`rec-bill-line-${index}-description`}
                    className="grid gap-1 text-xs font-medium"
                  >
                    What is billed
                    <Input
                      id={`rec-bill-line-${index}-description`}
                      value={line.description}
                      onChange={(e) =>
                        updateLine(index, { description: e.target.value })
                      }
                      required
                      placeholder="Monthly cleaning contract"
                      disabled={submitting}
                    />
                  </label>
                  <label
                    htmlFor={`rec-bill-line-${index}-amount`}
                    className="grid gap-1 text-xs font-medium"
                  >
                    Amount (USD)
                    <Input
                      id={`rec-bill-line-${index}-amount`}
                      type="number"
                      value={line.amount}
                      onChange={(e) =>
                        updateLine(index, { amount: e.target.value })
                      }
                      required
                      placeholder="0.00"
                      step="0.01"
                      min="0"
                      inputMode="decimal"
                      disabled={submitting}
                    />
                  </label>
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={() => removeLine(index)}
                    disabled={submitting || draft.lines.length <= 1}
                    aria-label={`Remove line ${index + 1}`}
                  >
                    Remove
                  </Button>
                </div>
              </div>
            ))}
            <div>
              <Button
                type="button"
                variant="secondary"
                onClick={() =>
                  setDraft((prev) => ({
                    ...prev,
                    lines: [...prev.lines, emptyDraftLine()],
                  }))
                }
                disabled={submitting}
              >
                Add line
              </Button>
            </div>
          </fieldset>
          <p
            className="text-sm text-[var(--muted-foreground)]"
            aria-live="polite"
          >
            Per bill:{" "}
            <span className="font-semibold text-[var(--foreground)]">
              {formatMoney({ cents: previewMinor, currency: "USD" })}
            </span>
          </p>
        </form>
      </Drawer>

      <Dialog
        open={confirmRun}
        onOpenChange={(open) => {
          if (!open && !running) setConfirmRun(false);
        }}
        title="Run all due plans?"
        description="Generates and posts a bill for every repeat-cost plan due as of today — across the whole organization, not just one plan. Each bill is drafted, received, and posted to the ledger."
      >
        <div className="actions-row">
          <Button
            type="button"
            variant="secondary"
            onClick={() => setConfirmRun(false)}
            disabled={running}
          >
            Cancel
          </Button>
          <Button type="button" onClick={() => void runNow()} disabled={running}>
            {running ? "Running…" : "Run all due plans"}
          </Button>
        </div>
      </Dialog>
    </div>
  );
}
